import { useLingui } from "@lingui-solid/solid/macro";
import { API } from "stoat.js";

function parseJsonObject(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== "string") return;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return;

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Ignore non-JSON messages.
  }
}

function normalizeError(error: unknown): unknown {
  if (!error) return error;

  if (typeof error === "string") {
    return parseJsonObject(error) ?? error;
  }

  if (typeof error !== "object") return error;

  const direct = error as Record<string, unknown>;

  if (typeof direct.type === "string") {
    return error;
  }

  const fromMessage = parseJsonObject(direct.message);
  if (typeof fromMessage?.type === "string") {
    return fromMessage;
  }

  const fromData =
    (direct.data as Record<string, unknown> | undefined) ??
    ((direct.response as Record<string, unknown> | undefined)?.data as
      | Record<string, unknown>
      | undefined) ??
    (direct.error as Record<string, unknown> | undefined);
  if (typeof fromData?.type === "string") {
    return fromData;
  }

  return error;
}

/**
 * The API error type and detail carried by any thrown value, if it has one
 */
export function getApiErrorInfo(error: unknown): {
  type?: string;
  detail?: string;
} {
  const normalized = normalizeError(error) as
    | { type?: unknown; detail?: unknown }
    | undefined;
  return {
    type: typeof normalized?.type === "string" ? normalized.type : undefined,
    detail:
      typeof normalized?.detail === "string" ? normalized.detail : undefined,
  };
}

/**
 * Where an error happened, for the errors whose plain wording would mislead:
 * - "mfa": while confirming with a password, authenticator code or recovery code
 * - "recovery_reset": while replacing recovery codes
 */
export type ErrorContext =
  | { context: "mfa"; method?: string }
  | { context: "recovery_reset" };

/**
 * Attach context to an API error before showing it
 */
export function withErrorContext(error: unknown, context: ErrorContext) {
  const { type, detail } = getApiErrorInfo(error);
  if (!type) return error;
  return { type, detail, ...context };
}

/**
 * Translate any error
 */
export function useError() {
  const { t } = useLingui();

  function toNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  function resolveRetryAfterMs(error: unknown): number | undefined {
    if (!error || typeof error !== "object") return;
    const direct = error as Record<string, unknown>;

    const directRetry = toNumber(direct.retry_after);
    if (directRetry !== undefined) return Math.max(0, directRetry);

    const data = direct.data as Record<string, unknown> | undefined;
    const dataRetry = toNumber(data?.retry_after);
    if (dataRetry !== undefined) return Math.max(0, dataRetry);

    const response = direct.response as Record<string, unknown> | undefined;
    const responseData = response?.data as Record<string, unknown> | undefined;
    const responseRetry = toNumber(responseData?.retry_after);
    if (responseRetry !== undefined) return Math.max(0, responseRetry);

    const headerValue =
      (
        (response?.headers as { get?: (name: string) => string | null } | undefined)
          ?.get?.("x-ratelimit-reset-after") ??
        (response?.headers as Record<string, unknown> | undefined)?.[
          "x-ratelimit-reset-after"
        ]
      ) ?? undefined;
    const headerRetry = toNumber(headerValue);
    if (headerRetry !== undefined) return Math.max(0, headerRetry);
  }

  return (error: unknown) => {
    const normalizedError = normalizeError(error);

    // Errors of the account security routes. Checked by name because several
    // of them are not part of the generated API error types.
    const security = normalizedError as
      | {
          type?: unknown;
          detail?: unknown;
          context?: unknown;
          method?: unknown;
          with?: unknown;
          retry_after?: unknown;
        }
      | undefined;
    if (security && typeof security === "object") {
      const inMfa = security.context === "mfa";
      switch (security.type) {
        case "TooManyRequests": {
          // `retry_after` is in seconds here.
          const seconds = Math.max(
            1,
            Math.ceil(toNumber(security.retry_after) ?? 1),
          );
          return t`Too many attempts. Please wait ${seconds} seconds and try again.`;
        }
        case "InvalidCredentials":
          if (inMfa) {
            if (security.detail === "code_already_used") {
              return t`That code was already used. Wait for your authenticator to show the next one.`;
            }
            if (security.method === "Password") return t`Wrong password.`;
            if (security.method === "Recovery") return t`Wrong recovery code.`;
            return t`Wrong code.`;
          }
          break;
        case "LockedOut":
          if (inMfa) {
            return t`Too many failed attempts. This account is locked for a while; please wait and try again.`;
          }
          break;
        case "InvalidToken":
          return t`This confirmation has expired or was already used. Please try again.`;
        case "DisallowedMFAMethod":
          return t`That method can't be used to confirm this action on your account.`;
        case "ServiceUnavailable":
          if (security.context === "recovery_reset") {
            return t`The service is temporarily unavailable. Your previous recovery codes may already be invalid: generate a new set as soon as it is back.`;
          }
          return t`The service is temporarily unavailable. Please try again in a moment.`;
        case "Gone":
          return t`This is no longer available.`;
        case "IncorrectData":
          // New password / new email refused by the server's policy. (Too
          // short and compromised passwords arrive as ShortPassword and
          // CompromisedPassword, which have their own messages below.)
          if (security.with === "password") {
            return t`That password can't be used. Please choose a different one.`;
          }
          if (security.with === "email") {
            return t`That email address can't be used.`;
          }
          break;
        case "InvalidOperation":
          switch (security.detail) {
            case "no_second_factor":
              return t`Set up an authenticator app first.`;
            case "no_pending_secret":
              return t`Authenticator setup has expired or was started somewhere else. Please start setup again.`;
            case "conflict":
              return t`Your security settings changed while this was in progress. Please try again.`;
            case "invalid_request":
              return t`That request was not valid. Please try again.`;
          }
          break;
      }
    }

    const retryAfterMs = resolveRetryAfterMs(normalizedError);

    if (retryAfterMs !== undefined) {
      const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
      return `You're doing that too quickly. Please wait ${seconds} seconds and try again.`;
    }

    // TODO: HTTP errors

    // handle BoopBoop API errors
    if (
      (normalizedError as { type?: never } | undefined)?.type &&
      typeof (normalizedError as { type: never }).type === "string"
    ) {
      const err = normalizedError as
        | API.Error
        | Exclude<
            API.Authifier_Error,
            | { type: "UnknownUser" }
            | { type: "DatabaseError" }
            | { type: "InternalError" }
          >;

      switch (err.type) {
        case "AlreadyFriends":
          return t`Already friends with this user.`;
        case "AlreadyInGroup":
          return t`You're already part of this group.`;
        case "AlreadyInServer":
          return t`You're already part of this server.`;
        case "AlreadyOnboarded":
          return t`Your user has already been created? Try logging in again or refreshing the app.`;
        case "AlreadyPinned":
          return t`This message is already pinned.`;
        case "AlreadySentRequest":
          return t`You've already sent a request to this user.`;
        case "Banned":
          return t`You are banned from this server.`;
        case "Blocked":
          return t`You have this user blocked.`;
        case "BlockedByOther":
          return t`This user has blocked you.`;
        case "BotIsPrivate":
          return t`This bot is private and can only be added by the creator.`;
        case "CannotEditMessage":
          return t`Cannot edit this message.`;
        case "CannotGiveMissingPermissions":
          return t`You cannot give yourself missing permissions.`;
        case "CannotJoinCall":
          return t`You cannot join this call.`;
        case "CannotRemoveYourself":
          return t`You cannot remove yourself.`;
        case "CannotReportYourself":
          return t`You cannot report yourself.`;
        case "CannotTimeoutYourself":
          return t`You cannot timeout yourself.`;
        case "DatabaseError":
          return t`Database error, please contact support. (${err.location})`;
        case "DiscriminatorChangeRatelimited":
          return t`Your discriminator change has been ratelimited, please try again later.`;
        case "DuplicateNonce":
          return t`This has already been sent.`;
        case "EmptyMessage":
          return t`This message is empty and has not been sent.`;
        case "FailedValidation":
          return t`Something is wrong with your request, ${err.error}.`;
        case "FeatureDisabled":
          return t`This feature is currently disabled.`;
        case "FileTypeNotAllowed":
          return t`This file type is not allowed.`;
        case "GroupTooLarge":
          return t`This group is too large, you can have up to ${err.max} users.`;
        case "ImageProcessingFailed":
          return t`Failed to process the image you provided.`;
        case "InternalError":
          return t`An internal error occurred. (${err.location})`;
        case "InvalidInvite":
          return t`This invite code is invalid. Please check it and try again.`.replace(
            ". ",
            ".\n",
          );
        case "InvalidCredentials":
          return t`Provided email or password is wrong.`;
        case "InvalidSession":
          return t`Please log in again.`;
        case "InvalidUsername":
          return t`This username is not allowed.`;
        case "MissingInvite":
          return t`An invite code is required to create an account.`;
        case "MissingPermission":
        case "MissingUserPermission":
          return t`You do not have permission to do this.`;
        case "NoEffect":
          return t`That action had no effect.`;
        case "NotElevated":
          return t`Your role ranking is too low to take this action.`;
        case "NotFound":
          return t`Could not find what you requested.`;
        case "ReachedMaximumBots":
          return t`You've reached your personal bot limit.`;
        case "UsernameTaken":
          return t`This username is already taken.`;
        case "TooManyEmoji":
          return t`You can't have more than ${err.max} emojis on this server.`;
        case "TooManyChannels":
          return t`You can't have more than ${err.max} channels on this server.`;
        case "TooManyServers":
          return t`You can't be in more than ${err.max} servers, please leave one and try again.`;
        case "TooManyPendingFriendRequests":
          return t`You've sent too many friend requests, the maximum is ${err.max}`;
        case "PayloadTooLarge":
          return "max" in err && typeof err.max === "number"
            ? `Message length is too high. Your tier limit is ${err.max} characters.`
            : "Message length is too high.";
        case "ShortPassword":
          return t`The password is too short.`;
        case "LockedOut":
          return t`You have been locked out for entering a wrong password multiple times. Please wait a couple minutes and try again.`;
        case "CompromisedPassword":
          return t`This password has previously appeared in security leaks, please use another password.`;
        case "UnverifiedAccount":
          return t`This account is not activated! Please check your account's inbox and try again.`;
        case "TotpAlreadyEnabled":
          return t`Multi-factor authentication is already enabled for this account.`;

        // unreachable errors (in theory)
        case "FileTooLarge":
        case "FileTooSmall":
        case "InvalidFlagValue":
        case "InvalidOperation":
        case "InvalidProperty":
        case "InvalidRole":
        case "IsBot":
        case "IsNotBot":
        case "LabelMe":
        case "NoEmbedData":
        case "NotAuthenticated":
        case "NotFriends":
        case "NotInGroup":
        case "NotOwner":
        case "NotPinned":
        case "NotPrivileged":
        case "ProxyError":
        case "TooManyAttachments": // todo: maybe handle these:
        case "TooManyEmbeds":
        case "TooManyReplies":
        case "TooManyRoles": // ... to here
        case "UnknownAttachment":
        case "UnknownChannel":
        case "UnknownMessage":
        case "UnknownServer":
        case "UnknownUser":
        case "VosoUnavailable":
          return err.type + " " + err.location;

        default:
          return t`Uncaught BoopBoop error: ${err.type}`;
      }
    }

    // pass-through pre-localised errors with new Error({ message: <> })
    if (
      (normalizedError as { message?: never } | undefined)?.message &&
      typeof (normalizedError as { message: never }).message === "string"
    ) {
      const message = (normalizedError as { message: string }).message.trim();
      if (message) return message;
    }

    return t`Something went wrong! ${normalizedError}`;
    // revert to `Try again later.` later
    // need to capture envelopes properly
  };
}
